// ─────────────────────────────────────────────────────────
// services/syncEngine.ts — Phase 6 sync coordinator
//
// Responsibilities:
//   1. Track last successful sync timestamp (persisted to IDB meta)
//   2. Expose queue size as a reactive signal (polled via hook)
//   3. syncNow() — flush queue + invalidate TanStack Query cache
//   4. registerReconnectHandler() — called once from useOffline
//      so there's a single canonical reconnect path
//
// Architecture:
//   flushQueue (offline.ts) handles the raw IDB write-queue
//   syncNow() orchestrates: flush → refetch → update last-sync ts
// ─────────────────────────────────────────────────────────
import { queryClient }  from '@/lib/queryClient'
import { QUERY_KEYS }   from '@/lib/queryKeys'
import { FLAGS }        from '@/lib/flags'
import { useUIStore }   from '@/store/uiStore'
import { useAuthStore } from '@/store/authStore'
import { flushQueue, queueSize as rawQueueSize } from './offline'

// ── Last-sync persistence (IDB meta) ─────────────────────
const LAST_SYNC_KEY = 'cbd_last_sync'

export function getLastSyncAt(): Date | null {
  try {
    const v = localStorage.getItem(LAST_SYNC_KEY)
    return v ? new Date(v) : null
  } catch { return null }
}

function setLastSyncAt(d: Date): void {
  try { localStorage.setItem(LAST_SYNC_KEY, d.toISOString()) } catch { /* ignore */ }
}

// ── Queue size (refreshed on demand) ─────────────────────
let _cachedQueueSize = 0
let _queuePollTimer: ReturnType<typeof setTimeout> | null = null

export function getCachedQueueSize(): number { return _cachedQueueSize }

async function refreshQueueSize(): Promise<void> {
  _cachedQueueSize = await rawQueueSize()
}

/** Start polling queue size every 8 s (stops when no listeners) */
export function startQueuePoll(): () => void {
  void refreshQueueSize()
  _queuePollTimer = setInterval(() => { void refreshQueueSize() }, 8_000)
  return () => {
    if (_queuePollTimer != null) { clearInterval(_queuePollTimer); _queuePollTimer = null }
  }
}

// ── syncNow() ────────────────────────────────────────────
// Full sync cycle:
//   1. Flush any queued offline writes to Supabase
//   2. Invalidate TanStack Query company-data cache → background refetch
//   3. Update last-sync timestamp
//
// Safe to call while offline — flushQueue() no-ops if navigator.onLine=false
export async function syncNow(companyId: string): Promise<void> {
  if (!FLAGS.USE_SUPABASE_DB) return

  const { setSyncing } = useUIStore.getState()

  setSyncing(true, 'Syncing…')
  try {
    await flushQueue()
    await refreshQueueSize()

    if (companyId) {
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.companyData(companyId),
      })
    }

    setLastSyncAt(new Date())
    setSyncing(true, 'Synced ✓')
    setTimeout(() => setSyncing(false), 2_000)
  } catch (e) {
    console.warn('[syncEngine] syncNow error:', e)
    setSyncing(true, 'Sync error — retrying on reconnect')
    setTimeout(() => setSyncing(false), 3_000)
    throw e
  }
}

// ── Reconnect handler registration ───────────────────────
// Registered once from useOffline (or main.tsx).
// Skips sync if no user is logged in.
let _reconnectRegistered = false

export function registerReconnectHandler(): () => void {
  if (_reconnectRegistered) return () => {}
  _reconnectRegistered = true

  const handler = async () => {
    const { user } = useAuthStore.getState()
    if (!user?.companyId) return
    try {
      await syncNow(user.companyId)
    } catch { /* errors already surfaced in syncNow */ }
  }

  window.addEventListener('online', handler)
  return () => {
    window.removeEventListener('online', handler)
    _reconnectRegistered = false
  }
}

// ── Reset (called on logout) ──────────────────────────────
export function resetSyncEngine(): void {
  if (_queuePollTimer != null) { clearInterval(_queuePollTimer); _queuePollTimer = null }
  _cachedQueueSize     = 0
  _reconnectRegistered = false
  try { localStorage.removeItem(LAST_SYNC_KEY) } catch { /* ignore */ }
  // Clear any persistent sync-error banner
  import('@/store/uiStore').then(m => m.useUIStore.getState().clearSyncError()).catch(() => {})
}
